/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Warning, ArrowsClockwise, Trash, ArrowsCounterClockwise, CheckCircle, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useDiagnostics } from "../lib/diagnostics";

const ACTIONS = [
  {
    key: "competitions",
    endpoint: "/admin/reset/competitions",
    phrase: "CLEAR COMPETITIONS",
    title: "Clear competitions",
    description: "Archive every competition record. Operators, admin users and provider keys are untouched.",
    confirmTone: "amber",
  },
  {
    key: "operators",
    endpoint: "/admin/reset/operators",
    phrase: "CLEAR OPERATORS",
    title: "Clear operators",
    description: "Hard-delete operators and cascade-archive their competitions. Provider keys and admin users are untouched.",
    confirmTone: "red",
  },
  {
    key: "all",
    endpoint: "/admin/reset/all",
    phrase: "CLEAR ALL",
    title: "Clear all discovery data",
    description: "Hard-delete operators, candidates and ingest runs; archive (or hard-delete) competitions. Admin users + provider keys survive.",
    confirmTone: "red",
  },
  {
    key: "resync-focus",
    endpoint: "/admin/reset/resync-focus",
    phrase: "RESYNC FOCUS",
    title: "Resync focus operators",
    description: "Re-seed the seven focus operators (BOTB, Elite, LLF, Dream Car Giveaways, Feel Good, Click, 7Days) and flip ingest_enabled on focus / off non-focus.",
    confirmTone: "amber",
  },
];

const FOCUS_OPERATORS = [
  "Elite Competitions", "BOTB", "LLF Games", "Dream Car Giveaways",
  "Feel Good Competitions", "Click Competitions", "7days Performance",
];

export default function ResetPanel() {
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(null);
  const [phraseInput, setPhraseInput] = useState({});
  const [hardDelete, setHardDelete] = useState({});
  const [backfillPreview, setBackfillPreview] = useState(null);
  const { diag } = useDiagnostics();

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/reset/preview");
      setCounts(data);
    } catch (e) {
      toast.error("Failed to load reset preview");
    }
  };
  useEffect(() => { refresh(); }, []);

  const runReset = async (action) => {
    const typed = (phraseInput[action.key] || "").trim();
    if (typed !== action.phrase) {
      toast.error(`Type "${action.phrase}" exactly to confirm.`);
      return;
    }
    if (diag?.environment === "production" && !window.confirm(`PRODUCTION DB.\n\n${action.title}. This cannot be undone (apart from re-running sync). Type-confirmation already received. Are you absolutely sure?`)) {
      return;
    }
    setBusy(action.key);
    try {
      const { data } = await api.post(action.endpoint, {
        confirmation_phrase: typed,
        hard_delete: !!hardDelete[action.key],
        note: "Admin reset panel",
      });
      toast.success(`${action.title} done`);
      console.info("Reset result:", data);
      setPhraseInput({ ...phraseInput, [action.key]: "" });
      refresh();
    } catch (e) {
      toast.error("Reset failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  const runBackfill = async (apply = false) => {
    setBusy(apply ? "backfill-apply" : "backfill-dry");
    try {
      const { data } = await api.post(
        "/admin/reset/backfill-categories-and-links",
        { dry_run: !apply, only_brave: false },
      );
      setBackfillPreview(data);
      const totalCat = Object.values(data.category_changes || {}).reduce((a, b) => a + b, 0);
      const totalLink = Object.values(data.link_type_changes || {}).reduce((a, b) => a + b, 0);
      toast.success(
        `${apply ? "Applied" : "Dry-run"} backfill: ${totalCat} category fix(es), ${totalLink} link-type fix(es) across ${data.scanned} records.`,
      );
    } catch (e) {
      toast.error("Backfill failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  if (!counts) return <div className="text-white/60">Loading reset preview…</div>;

  return (
    <div className="space-y-6" data-testid="admin-reset-panel">
      <div className="rounded-lg border border-red-500/30 bg-red-500/[0.05] p-4 flex gap-3">
        <Warning size={20} className="text-red-300 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <div className="font-semibold text-red-100">Destructive zone</div>
          <p className="text-white/70 mt-1">
            These actions modify or remove discovery data. Admin users and provider credentials always survive.
            Each action requires you to <strong className="text-white">type</strong> the exact confirmation phrase
            below — clicking is not enough.
          </p>
        </div>
      </div>

      {/* Counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {[
          { k: "operators_total", label: "Operators" },
          { k: "operators_focus", label: "· Focus ops" },
          { k: "operators_non_focus", label: "· Non-focus ops" },
          { k: "competitions_total", label: "Competitions" },
          { k: "competitions_verified_live", label: "· Verified live" },
          { k: "competitions_archived", label: "· Archived" },
          { k: "competitions_seed_demo", label: "· Seed demo" },
          { k: "discovered_candidates_total", label: "Candidates" },
          { k: "ingest_runs_total", label: "Ingest runs" },
          { k: "users_total", label: "Users (untouched)" },
        ].map((s) => (
          <div
            key={s.k}
            className="rounded-md px-3 py-2 text-xs border border-white/10 bg-white/[0.03] text-white/70"
            data-testid={`reset-count-${s.k}`}
          >
            <div className="text-[10px] uppercase opacity-60">{s.label}</div>
            <div className="text-base font-semibold text-white">{counts[s.k]}</div>
          </div>
        ))}
      </div>

      {/* Reset actions */}
      <div className="space-y-3">
        {ACTIONS.map((a) => (
          <div
            key={a.key}
            className={`rounded-lg border p-4 ${a.confirmTone === "red" ? "border-red-500/30 bg-red-500/[0.04]" : "border-amber-500/30 bg-amber-500/[0.04]"}`}
            data-testid={`reset-action-${a.key}`}
          >
            <div className="flex items-start gap-3">
              {a.key === "resync-focus" ? (
                <ArrowsCounterClockwise size={18} weight="duotone" className={a.confirmTone === "red" ? "text-red-300" : "text-amber-300"} />
              ) : (
                <Trash size={18} weight="duotone" className={a.confirmTone === "red" ? "text-red-300" : "text-amber-300"} />
              )}
              <div className="flex-1">
                <div className="text-sm font-semibold text-white">{a.title}</div>
                <p className="text-[11px] text-white/60 mt-0.5 leading-relaxed">{a.description}</p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    value={phraseInput[a.key] || ""}
                    onChange={(e) => setPhraseInput({ ...phraseInput, [a.key]: e.target.value })}
                    placeholder={`Type "${a.phrase}"`}
                    data-testid={`reset-phrase-${a.key}`}
                    className="bg-white/5 border border-white/20 rounded px-3 py-1.5 text-xs font-mono-num w-72 outline-none focus:border-[var(--cc-gold)]"
                  />
                  {(a.key === "competitions" || a.key === "all") && (
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-white/60">
                      <input
                        type="checkbox"
                        checked={!!hardDelete[a.key]}
                        onChange={(e) => setHardDelete({ ...hardDelete, [a.key]: e.target.checked })}
                        data-testid={`reset-hard-delete-${a.key}`}
                      />
                      Hard delete (cannot recover)
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => runReset(a)}
                    disabled={busy === a.key || (phraseInput[a.key] || "").trim() !== a.phrase}
                    data-testid={`reset-apply-${a.key}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded inline-flex items-center gap-1.5 disabled:opacity-40 ${
                      a.confirmTone === "red"
                        ? "bg-red-500/30 hover:bg-red-500/40 text-red-100"
                        : "bg-amber-500/30 hover:bg-amber-500/40 text-amber-100"
                    }`}
                  >
                    {busy === a.key ? "Running…" : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Backfill */}
      <div
        className="rounded-lg border border-sky-500/30 bg-sky-500/[0.05] p-4"
        data-testid="reset-backfill-card"
      >
        <div className="flex items-start gap-3">
          <Sparkle size={18} weight="duotone" className="text-sky-300" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-sky-100">
              Normalise categories &amp; source link types
            </div>
            <p className="text-[11px] text-white/60 mt-0.5 leading-relaxed">
              Maps operator-supplied category strings ("Vehicle", "Supercar", "Watch") onto the canonical
              <code className="text-[var(--cc-gold)]"> cars / bikes / watches / cash / tech / gaming / lifestyle / luxury / other</code> set,
              and labels every source URL as <code>deep_competition_url</code>, <code>competition_index_url</code>,
              <code>operator_base_url</code> or <code>unknown</code> so the public CTA doesn't lie.
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => runBackfill(false)}
                disabled={busy === "backfill-dry"}
                data-testid="reset-backfill-dry-btn"
                className="px-3 py-1.5 text-xs font-semibold rounded bg-white/10 hover:bg-white/15 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <ArrowsClockwise size={12} /> Dry-run
              </button>
              <button
                type="button"
                onClick={() => runBackfill(true)}
                disabled={busy === "backfill-apply"}
                data-testid="reset-backfill-apply-btn"
                className="px-3 py-1.5 text-xs font-semibold rounded bg-sky-500/30 hover:bg-sky-500/40 text-sky-100 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <CheckCircle size={12} weight="bold" /> Apply backfill
              </button>
            </div>
            {backfillPreview && (
              <div className="mt-3 text-[11px] text-white/70 space-y-1">
                <div>Scanned <span className="text-white">{backfillPreview.scanned}</span> records · {backfillPreview.dry_run ? "dry-run" : "applied"}</div>
                {Object.entries(backfillPreview.category_changes || {}).map(([k, v]) => (
                  <div key={k}>· {v} × category change: <code className="text-amber-200">{k}</code></div>
                ))}
                {Object.entries(backfillPreview.link_type_changes || {}).map(([k, v]) => (
                  <div key={k}>· {v} × link-type change: <code className="text-sky-200">{k}</code></div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Focus operator info */}
      <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 text-[11px] text-white/60">
        <strong className="text-white">Focus operators (iter 18):</strong>{" "}
        {FOCUS_OPERATORS.join(" · ")}.
        After <code>RESYNC FOCUS</code> the non-focus operators have <code>ingest_enabled=false</code>.
      </div>
    </div>
  );
}
